import { useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import {
    ChevronLeft,
    Loader2,
    Download,
    Upload,
    FileCheck,
    CheckCircle2,
    AlertCircle,
    X,
    FileText,
    User as UserIcon,
} from 'lucide-react';
import { YourCheckaNavigation } from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useAppSelector } from '@/hooks/useAppStore';
import { selectCurrentUser } from '@/store/slices/authSlice';
import { useGetPoliceInvestigationQuery, useUploadPoliceCharacterCertificateMutation } from '@/store/api/apiSlice';
import { resolveFileUrl } from '@/utils/fileUrl';
import { parseErrorMessage } from '@/utils/parseErrorMessage';
import { validateInvestigationFile } from '@/utils/fileValidation';
import type { PoliceFile } from '@/types';

const API_BASE = ((import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '/api/v1').replace(/\/+$/, '');

function getInitials(firstName?: string, lastName?: string) {
    return `${firstName?.charAt(0) ?? ''}${lastName?.charAt(0) ?? ''}`.toUpperCase() || 'PL';
}

function formatWhen(value?: string | null): string {
    if (!value) return '';
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? '' : d.toLocaleString();
}

function formatDate(value?: string | null): string {
    if (!value) return '';
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString();
}

// Public /uploads/* file — download without an auth header (the header would force
// a CORS preflight the static host doesn't answer).
async function downloadPublicFile(file: PoliceFile, onError: (m: string) => void) {
    const absoluteUrl = resolveFileUrl(file.fileUrl);
    try {
        const res = await fetch(absoluteUrl, { method: 'GET' });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = file.fileName || 'file';
        document.body.appendChild(a);
        a.click();
        setTimeout(() => {
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        }, 1000);
    } catch {
        const opened = window.open(absoluteUrl, '_blank', 'noopener,noreferrer');
        if (!opened) onError('Could not download the file. Please try again.');
    }
}

// The branded profile PDF is an authed API route (not a public /uploads file), so
// fetch it with the Bearer token and download the blob — a plain window.open would
// not carry the auth header.
async function downloadProfilePdf(id: string, employeeName: string, onError: (m: string) => void) {
    const token = localStorage.getItem('access_token') || '';
    try {
        const res = await fetch(`${API_BASE}/police/investigations/${id}/profile.pdf`, {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${(employeeName || 'employee').replace(/\s+/g, '-').toLowerCase()}-profile.pdf`;
        document.body.appendChild(a);
        a.click();
        setTimeout(() => {
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        }, 1000);
    } catch {
        onError('Could not download the profile PDF. Please try again.');
    }
}

export default function PoliceDetail() {
    const { id = '' } = useParams();
    const navigate = useNavigate();
    const currentUser = useAppSelector(selectCurrentUser);

    const { data, isLoading, isError } = useGetPoliceInvestigationQuery(id, { skip: !id });
    const [uploadCertificate, { isLoading: isUploading }] = useUploadPoliceCharacterCertificateMutation();

    const inputRef = useRef<HTMLInputElement>(null);
    const [selected, setSelected] = useState<File | null>(null);
    const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const handlePick = (file: File | null) => {
        setError('');
        setSuccess('');
        if (!file) {
            setSelected(null);
            return;
        }
        const validationError = validateInvestigationFile(file);
        if (validationError) {
            setError(validationError);
            if (inputRef.current) inputRef.current.value = '';
            return;
        }
        setSelected(file);
    };

    const handleUpload = async () => {
        if (!selected || !id) return;
        setError('');
        try {
            await uploadCertificate({ id, file: selected }).unwrap();
            setSelected(null);
            if (inputRef.current) inputRef.current.value = '';
            setSuccess('Police character certificate uploaded. This check is now marked complete.');
        } catch (err: any) {
            setError(parseErrorMessage(err, 'Could not upload the certificate. Please try again.'));
        }
    };

    const handleDownloadPdf = async () => {
        if (!id) return;
        setError('');
        setIsDownloadingPdf(true);
        await downloadProfilePdf(id, data?.employee.name ?? '', setError);
        setIsDownloadingPdf(false);
    };

    return (
        <main className="w-full min-h-screen flex flex-col px-4 py-6 md:px-6 lg:px-8 xl:px-12 bg-[#EDEFF3]">
            <div className="bg-white rounded-3xl shadow-sm flex-1 flex flex-col overflow-hidden">
                <YourCheckaNavigation
                    showTitle={false}
                    showInfo={false}
                    showLocation={false}
                    notificationCount={0}
                    userName={currentUser ? `${currentUser.firstName} ${currentUser.lastName}` : 'Police'}
                    userInitials={currentUser ? getInitials(currentUser.firstName, currentUser.lastName) : 'PL'}
                    only={true}
                    className="border-b border-gray-100"
                />

                <div className="flex-1 overflow-y-auto p-4 md:p-8 lg:p-10">
                    <button
                        onClick={() => navigate('/police/investigations')}
                        className="mb-5 inline-flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-900"
                    >
                        <ChevronLeft className="w-4 h-4" />
                        Back to assignments
                    </button>

                    {isLoading ? (
                        <div className="flex items-center justify-center py-24">
                            <Loader2 className="h-7 w-7 text-gray-400 animate-spin" />
                        </div>
                    ) : isError || !data ? (
                        <div className="max-w-md mx-auto text-center py-16">
                            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-red-50">
                                <AlertCircle className="h-7 w-7 text-red-500" />
                            </div>
                            <h1 className="text-lg font-bold text-gray-900">Assignment not available</h1>
                            <p className="mt-1 text-sm text-gray-500">
                                This investigation isn't assigned to you, or the link is invalid.
                            </p>
                        </div>
                    ) : (
                        <div className="max-w-3xl mx-auto flex flex-col gap-6">
                            {/* Header */}
                            <div className="flex flex-wrap items-center justify-between gap-3">
                                <div>
                                    <h1 className="text-2xl font-bold text-gray-900">
                                        {data.employee.name || 'Employee'}
                                    </h1>
                                    <p className="text-sm text-gray-500">Police character check</p>
                                </div>
                                {data.policeCompletedAt ? (
                                    <Badge
                                        variant="outline"
                                        className="bg-green-50 text-green-700 border-green-100 px-3 py-1 rounded-lg font-medium"
                                    >
                                        Police check complete ✓ {formatDate(data.policeCompletedAt)}
                                    </Badge>
                                ) : (
                                    <Badge
                                        variant="outline"
                                        className="bg-amber-50 text-amber-700 border-amber-100 px-3 py-1 rounded-lg font-medium"
                                    >
                                        Pending
                                    </Badge>
                                )}
                            </div>

                            {/* Profile + photo */}
                            <div className="rounded-2xl border border-gray-100 p-5">
                                <div className="flex flex-col sm:flex-row gap-5">
                                    <div className="shrink-0">
                                        {data.photograph ? (
                                            <img
                                                src={resolveFileUrl(data.photograph.fileUrl)}
                                                alt={data.employee.name || 'Photograph'}
                                                className="h-28 w-28 rounded-xl border border-gray-200 object-cover"
                                            />
                                        ) : (
                                            <div className="flex h-28 w-28 items-center justify-center rounded-xl border border-dashed border-gray-200 bg-gray-50">
                                                <UserIcon className="h-8 w-8 text-gray-300" />
                                            </div>
                                        )}
                                    </div>
                                    <div className="grid flex-1 grid-cols-1 gap-x-6 gap-y-1.5 sm:grid-cols-2">
                                        <Line label="Full name" value={data.employee.name} />
                                        <Line label="NIN" value={data.employee.nin} />
                                        <Line label="Address" value={data.employee.address} />
                                        <Line label="Telephone" value={data.employee.telephone} />
                                        <Line label="Email" value={data.employee.email} />
                                        <Line label="Sex" value={data.employee.sex} />
                                        <Line label="Date of birth" value={formatDate(data.employee.dateOfBirth)} />
                                        <Line label="Place of birth" value={data.employee.placeOfBirth} />
                                        <Line label="State of origin" value={data.employee.stateOfOrigin} />
                                        <Line label="Nationality" value={data.employee.nationality} />
                                    </div>
                                </div>

                                <div className="mt-5 border-t border-gray-100 pt-4">
                                    <Button
                                        onClick={handleDownloadPdf}
                                        disabled={isDownloadingPdf}
                                        className="flex items-center gap-2 rounded-lg bg-gray-900 px-5 py-2.5 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-50"
                                    >
                                        {isDownloadingPdf ? (
                                            <Loader2 className="h-4 w-4 animate-spin" />
                                        ) : (
                                            <FileText className="h-4 w-4" />
                                        )}
                                        Download employee profile (PDF)
                                    </Button>
                                </div>
                            </div>

                            {/* Upload character certificate */}
                            <div className="rounded-2xl border border-gray-100 p-5">
                                <h2 className="text-sm font-bold text-gray-900 mb-3">
                                    Upload the police character certificate
                                </h2>

                                {data.characterCertificate && (
                                    <div className="mb-4 flex items-center justify-between gap-3 rounded-xl border border-green-100 bg-green-50 px-3 py-2.5">
                                        <span className="flex items-center gap-2 min-w-0">
                                            <CheckCircle2 className="h-4 w-4 shrink-0 text-green-600" />
                                            <span className="truncate text-sm text-green-800">
                                                {data.characterCertificate.fileName}
                                            </span>
                                            <span className="hidden sm:inline text-xs text-green-600/70 shrink-0">
                                                {formatWhen(data.characterCertificate.uploadedAt)}
                                            </span>
                                        </span>
                                        <Button
                                            onClick={() => downloadPublicFile(data.characterCertificate!, setError)}
                                            className="shrink-0 flex items-center gap-1.5 rounded-lg border border-green-200 bg-white px-3 py-2 text-xs font-medium text-green-700 hover:bg-green-50"
                                        >
                                            <Download className="h-3.5 w-3.5" />
                                            Download
                                        </Button>
                                    </div>
                                )}

                                <div className="flex flex-col gap-3">
                                    {selected ? (
                                        <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2.5">
                                            <FileCheck className="h-4 w-4 shrink-0 text-green-600" />
                                            <span className="flex-1 truncate text-sm text-gray-800">
                                                {selected.name}
                                            </span>
                                            <button
                                                type="button"
                                                onClick={() => handlePick(null)}
                                                className="shrink-0 rounded-md p-1 text-red-500 hover:bg-red-50"
                                                aria-label="Remove file"
                                            >
                                                <X className="h-4 w-4" />
                                            </button>
                                        </div>
                                    ) : (
                                        <button
                                            type="button"
                                            onClick={() => inputRef.current?.click()}
                                            className="flex items-center gap-2 rounded-lg border border-dashed border-gray-300 bg-white px-3 py-2.5 text-left text-sm text-gray-600 transition-colors hover:border-[#FF6B35] hover:text-[#FF6B35]"
                                        >
                                            <Upload className="h-4 w-4 shrink-0" />
                                            <span className="flex-1">
                                                {data.characterCertificate
                                                    ? 'Replace certificate (PDF, Word or image)'
                                                    : 'Upload certificate (PDF, Word or image)'}
                                            </span>
                                        </button>
                                    )}

                                    <input
                                        ref={inputRef}
                                        type="file"
                                        accept="application/pdf,.pdf,.doc,.docx,image/jpeg,image/png"
                                        onChange={(e) => handlePick(e.target.files?.[0] ?? null)}
                                        className="hidden"
                                    />

                                    <Button
                                        onClick={handleUpload}
                                        disabled={!selected || isUploading}
                                        className="self-start flex items-center gap-2 rounded-lg bg-[#FF6B35] px-6 py-2.5 text-sm font-medium text-white hover:bg-[#e0581f] disabled:opacity-50"
                                    >
                                        {isUploading && <Loader2 className="h-4 w-4 animate-spin" />}
                                        {data.policeCompletedAt ? 'Replace certificate' : 'Upload & complete'}
                                    </Button>
                                </div>

                                {error && (
                                    <p className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600">{error}</p>
                                )}
                                {success && (
                                    <p className="mt-3 rounded-lg bg-green-50 px-3 py-2 text-sm text-green-700">
                                        {success}
                                    </p>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </main>
    );
}

function Line({ label, value }: { label: string; value?: string | undefined }) {
    return (
        <p className="text-sm">
            <span className="text-gray-500">{label}: </span>
            <span className="font-medium text-gray-900">{value || '—'}</span>
        </p>
    );
}
